import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";

const DAY_NAMES = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const DAY_NAMES_FULL = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

const HOUR_START = 8;
const HOUR_END = 22;

// Helpers — work in local time, no TZ surprises
function startOfWeek(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  // Monday-first week
  const day = (x.getDay() + 6) % 7; // 0=Mon ... 6=Sun
  x.setDate(x.getDate() - day);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function fmtISO(d) {
  // YYYY-MM-DD
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fmtRange(start) {
  const end = addDays(start, 6);
  const sm = start.toLocaleDateString("es-ES", { month: "short", day: "numeric" });
  const em = end.toLocaleDateString("es-ES", { month: "short", day: "numeric", year: "numeric" });
  return `${sm} – ${em}`;
}

function parseTime(t) {
  if (!t) return 0;
  const [h, m] = t.split(":").map((n) => parseInt(n, 10) || 0);
  return h + m / 60;
}

const WeekCalendar = ({ classes = [], onClassClick }) => {
  const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(anchor, i)),
    [anchor]
  );

  const classesByDay = useMemo(() => {
    const map = {};
    days.forEach((d) => {
      map[fmtISO(d)] = [];
    });
    classes.forEach((c) => {
      if (map[c.date] !== undefined) {
        map[c.date].push(c);
      }
    });
    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => parseTime(a.time) - parseTime(b.time));
    });
    return map;
  }, [classes, days]);

  const today = fmtISO(new Date());

  return (
    <div className="cal-shell" data-testid="week-calendar">
      <div className="cal-toolbar">
        <button
          onClick={() => setAnchor((a) => addDays(a, -7))}
          className="cal-nav"
          aria-label="Semana anterior"
          data-testid="cal-prev"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="cal-range">
          <CalendarIcon size={14} />
          <span data-testid="cal-range-label">{fmtRange(anchor)}</span>
        </div>
        <button
          onClick={() => setAnchor((a) => addDays(a, 7))}
          className="cal-nav"
          aria-label="Semana siguiente"
          data-testid="cal-next"
        >
          <ChevronRight size={16} />
        </button>
        <button
          onClick={() => setAnchor(startOfWeek(new Date()))}
          className="cal-today"
          data-testid="cal-today"
        >
          Hoy
        </button>
      </div>

      <div className="cal-grid">
        <div className="cal-col cal-col-hours">
          <div className="cal-day-head" />
          {Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i).map((h) => (
            <div key={h} className="cal-hour-cell">
              {String(h).padStart(2, "0")}:00
            </div>
          ))}
        </div>

        {days.map((d, i) => {
          const iso = fmtISO(d);
          const isToday = iso === today;
          const dayClasses = classesByDay[iso] || [];
          return (
            <div key={iso} className={`cal-col ${isToday ? "is-today" : ""}`}>
              <div className="cal-day-head">
                <span className="cal-day-name">{DAY_NAMES[i]}</span>
                <span className="cal-day-num">{d.getDate()}</span>
                {isToday && <span className="cal-today-dot" aria-hidden="true" />}
              </div>
              <div className="cal-day-body">
                {Array.from({ length: HOUR_END - HOUR_START }, (_, j) => (
                  <div key={j} className="cal-slot" />
                ))}
                {dayClasses.map((c) => {
                  const start = parseTime(c.time);
                  const top = Math.max(0, (start - HOUR_START)) * 48; // 48px per hour
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className={`cal-event cal-event-${c.category || "paid"} ${c.status === "impartida" ? "is-done" : ""}`}
                      style={{ top: `${top}px` }}
                      onClick={() => onClassClick && onClassClick(c)}
                      data-testid={`cal-event-${c.id}`}
                      title={`${DAY_NAMES_FULL[i]} ${c.time} — ${c.student_name}`}
                    >
                      <span className="cal-event-time">{c.time}</span>
                      <span className="cal-event-name">{c.student_name}</span>
                      <span className="cal-event-type">{c.class_type}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WeekCalendar;
